import * as React from "react";
import { BuilderContext, useDrawer } from "react-flow-builder";
import { useForm, useFieldArray } from "react-hook-form";
import { Text, Grid, Button, Modal, ActionIcon, Title, Divider } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { CTextInput, CSelect, CTextarea, CMultiSelect } from "@supportinitiative/react-hook-form-mantine";

import { ReconsetCVMContext } from "../../ReconsetCVM";
import Where from "../QueryMatch/Where";

import { yupResolver } from "@hookform/resolvers/yup";
import { useActiveRoles } from "../../../hooks";
import { getId } from "../../../utilities";
import { SummaryStatsActionSchema, SummaryStatsActionType } from "@supportinitiative/common";

function SummaryStats() {
  const { selectedNode: node } = React.useContext(BuilderContext);
  const { closeDrawer: cancel, saveDrawer: save } = useDrawer();
  const { state: reconsetFormState } = React.useContext(ReconsetCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SummaryStatsActionType>({
    defaultValues: node?.data || {
      name: "",
      header: "",
      value: "",
      valueType: "TEXT",
      increaseOrDecrease: "",
      additionalContext: "",
      color: "GREEN",
      dataPopulation: [],
    },
    criteriaMode: "all",
    resolver: yupResolver(SummaryStatsActionSchema),
  });

  const dataPopulation = useFieldArray({
    control,
    name: "dataPopulation",
  });

  return (
    <Modal centered size={"xl"} opened={true} onClose={cancel} title={<Title order={4}>Summary Stats</Title>}>
      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CTextInput
            name={`name`}
            control={control}
            showErrorText={false}
            label="Name"
            required
            disabled={node?.disabled || false}
          />
        </Grid.Col>
      </Grid>

      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={12}>
          <CSelect
            name={`type`}
            control={control}
            showErrorText={false}
            label="Type"
            data={[
              { label: "INFO", value: "INFO" },
              { label: "WARNING", value: "WARNING" },
              { label: "ERROR", value: "ERROR" },
            ]}
            required
            disabled={node?.disabled || false}
          />
        </Grid.Col>
      </Grid>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // borderTop: "2px solid #ccc",
          // paddingTop: "20px",
          // width: "90%",
        }}
      >
        <Divider my="xs" label="Data Population" labelPosition="center" />

        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Columns
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              dataPopulation.append({
                column: "",
                genFunction: "",
                id: getId(),
              });
            }}
            disabled={reconsetFormState.actionOptions.disableAll}
          >
            <IconPlus />
          </ActionIcon>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {dataPopulation.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`dataPopulation.${index}.column`}
                    control={control}
                    label={"Column"}
                    disabled={reconsetFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`dataPopulation.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={reconsetFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput name={`dataPopulation.${index}.id`} control={control} label={"Id"} disabled={true} />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      dataPopulation.remove(index);
                    }}
                    disabled={reconsetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // borderTop: "2px solid #ccc",
          // paddingTop: "20px",
          // width: "90%",
        }}
      >
        <Divider my="xs" label="Details" labelPosition="center" />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <CTextInput
                name={`header`}
                control={control}
                label="Header"
                required
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`value`}
                control={control}
                label="Value"
                required
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CSelect
                name={`valueType`}
                control={control}
                label="Value Type"
                data={[
                  { label: "Text", value: "TEXT" },
                  { label: "Date", value: "DATE" },
                  { label: "Amount", value: "AMOUNT" },
                  { label: "Integer", value: "INTEGER" },
                ]}
                showErrorText={false}
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextInput
                name={`additionalContext`}
                control={control}
                label="Additional Context"
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`increaseOrDecrease`}
                control={control}
                label="Increase or Decrease"
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <CSelect
                name={`color`}
                control={control}
                label="Color"
                data={[
                  { label: "Red", value: "RED" },
                  { label: "Pink", value: "PINK" },
                  { label: "Teal", value: "TEAL" },
                  { label: "Blue", value: "BLUE" },
                ]}
                showErrorText={false}
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`chartGroup`}
                control={control}
                label="Chart Group"
                disabled={reconsetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </div>
      </div>

      <Grid align="stretch" gutter={"xs"} mt="xs">
        <Grid.Col span={2}>
          <Button
            color="teal"
            variant="filled"
            fullWidth
            type="submit"
            onClick={
              node?.disabled
                ? () => {
                    cancel();
                  }
                : async (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    const values = getValues();
                    // console.log("values", values);
                    clearErrors();
                    try {
                      await SummaryStatsActionSchema.validate(values, {
                        abortEarly: false,
                      });

                      save?.(values);
                      //cancel();
                    } catch (error) {
                      console.log("errors", error);
                      console.log("error inner", JSON.stringify(error));
                      (error as any).inner.forEach((err: any) => {
                        setError(err.path, {
                          type: "custom",
                          message: err.message,
                        });
                      });
                    }
                  }
            }
          >
            Ok
          </Button>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}

export default SummaryStats;
